<template>
  <div class="device-page">
    <AlltestaTowerSettingsModal
      v-if="device != null"
      ref="settingsModal"
      :device-id="id"
      :isCromite="isCromite"
    />

    <template v-if="device != null && deviceState != null">
      <div class="top-line--flex device-page__wrapper-title">
        <div>
          <InputTitle
            v-if="device"
            v-model="device.name"
            iconName="input"
            data-test-id="page-title"
            @save="saveName"
          />

          <div class="pt-0 pb-2 mt-0">
            <template v-if="device.name !== ' '">
              <DeviceType :model="device.model" class="mr-2" />
              <DeviceConnection
                :hasDetails="true"
                :device="device"
                :deviceState="deviceState"
                :deviceSocket="socket"
                class="mr-2"
              />
              <FirmwareStatusMini
                v-if="device.current_version"
                :currentVersion="device.current_version"
                class="mr-2"
              />
              <StatusOnline
                :isOnline="device.is_online"
                :wasOnline="device.date_last_online"
                :isWorking="isInjectionInProgress"
                class="mr-2"
                data-test-id="device-status-online"
              />
            </template>
            <data-state-component v-else :updating="true" class="m-0 text-left" />
          </div>
        </div>
        <div class="top-line__actions" data-test-id="btn-show-settings">
          <button
            class="button--square-image"
            :disabled="state.injection_id !== null"
            @click="settings"
          >
            <i class="material-icons material-icon--18">settings</i>
            Settings
          </button>
        </div>
      </div>

      <SampleInjectionMiniComponent :injectionId="state.injection_id" />

      <DeviceHardwareComponent
        :device="device"
        :deviceState="deviceState"
        :method="method"
        :socket="socket"
        class="device-page__hardware"
      />

      <MethodCard
        :method="method"
        :configuration="configurationForMethod"
        :deviceId="device.id"
        :canEdit="device.is_online"
        :isMethodCompatible="device.isMethodCompatible"
        class="mt-12 top-line"
        @update:method="updateDeviceMethod"
      />
      <ColumnCard
        :column="column"
        class="mt-12 top-line"
        :canEdit="device.is_online"
        @update:column="updateDeviceColumn"
      />

      <div style="background-color: #00000044; height: 1px; margin: 64px 0 56px" />

      <div class="top-line--flex">
        <h4 style="margin: 0 0 4px 0">Recent Runs</h4>
        <SearchFakedInput
          style="float: right; margin-right: 0; vertical-align: middle"
          @mousedown="$router.push({ name: 'search', query: { device_id: id } })"
        />
      </div>

      <RunsListComponent style="margin-top: 24px" :runs="recentInjections" />
    </template>
    <error-component v-else-if="errorCode != null" :code="errorCode" style="margin-top: 64px" />
    <loading-component v-else />
  </div>
</template>

<script>
  import 'assets/css/base/icons.scss';

  import DeviceSocket, { DeviceSocketEvents } from 'api/sockets/DeviceSocket';
  import StatusOnline from '@/uikitProject/statuses/StatusOnline.vue';
  import DataStateComponent from 'components/element/DataStateComponent.vue';
  import SearchFakedInput from 'components/element/SearchFakedInput.vue';
  import DeviceType from '@/uikitProject/devices/DeviceType.vue';
  import DeviceConnection from '@/uikitProject/devices/vueDeviceConnection/DeviceConnection.vue';
  import RunsListComponent from '@/components/block/RunsListComponent.vue';
  import PageTitle from '@/mixins/page-title';
  import ErrorComponent from 'components/element/ErrorComponent';
  import LoadingComponent from 'components/element/LoadingComponent';
  import DeviceHardwareComponent from 'components/block/DeviceHardwareComponent';
  import SampleInjectionMiniComponent from 'components/block/SampleInjectionMiniComponent';
  import NavigatorHelper from 'utils/NavigatorHelper.ts';
  import ColumnCard from 'components/blocks/cards/ColumnCard';
  import MethodCard from 'components/blocks/cards/MethodCard';
  import FirmwareStatusMini from '@/uikitProject/devices/FirmwareStatusMini';
  import AlltestaTowerSettingsModal from 'components/block/modal/AlltestaTowerSettingsModal';
  import InputTitle from '@/uikitProject/inputs/inputTitle/InputTitle';
  import { apiDevices } from '@/api/graphql/cloud/devices';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { METHOD_RESET_REASONS } from '@/constants/hardware/eventReasons';

  const preventWhiteRectForMobileSafari = () => {
    if (NavigatorHelper.isMobile && NavigatorHelper.browserName === 'safari') {
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
      }, 400);
    }
  };

  export default {
    name: 'DevicePage',

    components: {
      InputTitle,
      AlltestaTowerSettingsModal,
      FirmwareStatusMini,
      MethodCard,
      ColumnCard,
      SampleInjectionMiniComponent,
      DeviceHardwareComponent,
      LoadingComponent,
      ErrorComponent,
      SearchFakedInput,
      DataStateComponent,
      RunsListComponent,
      DeviceConnection,
      DeviceType,
      StatusOnline,
    },

    mixins: [PageTitle],

    props: {
      id: String,
    },

    data() {
      return {
        pageTitle: 'HPLC Cloud: Device',

        device: null,

        method: null,
        column: null,

        recentInjections: null,

        socket: null,

        connectionLost: false,
        timerConnectionLost: null,

        errorCode: null,

        listenersGroupId: null,

        subscriptionForDevice: null,
        subscriptionForDeviceState: null,
        subscriptionForRecentInjections: null,

        deviceState: null,
      };
    },

    computed: {
      state() {
        return this.deviceState ?? {};
      },
      configurationForMethod() {
        return this.device.configuration?.configuration_for_method;
      },

      isCromite() {
        return this.device?.model === 'cmt';
      },

      isInjectionInProgress() {
        return this.deviceState?.injection_id != null;
      },
    },

    watch: {
      id: {
        handler(id) {
          this.socket = this.createDeviceSocket(id);

          this.initDeviceData(id);
        },
        immediate: true,
      },
      device(deviceNew, deviceOld) {
        if (deviceNew?.is_online !== false) {
          this.onConnectionConfirmed();
        }
        this.pageTitle = deviceNew?.name || 'HPLC Cloud: Device';

        if (
          deviceNew?.method_invalidated_reason != null &&
          deviceOld != null &&
          deviceOld.method_invalidated_reason == null
        ) {
          this.onMethodNotValid(deviceNew.method_invalidated_reason);
        }
      },
      connectionLost(connectionLost) {
        if (!connectionLost) {
          apiDevices.doNotSleep(this.id);
        }
      },
      state(s) {
        if (s.ready == null) return;
        if (s.sleep_countdown && s.sleep_countdown <= 1) {
          apiDevices.doNotSleep(this.id);
        }

        this.onConnectionConfirmed();
      },
    },

    beforeDestroy() {
      if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);
      this.destroyDeviceSocket();
      this.stopSubscriptions();
    },

    mounted() {
      preventWhiteRectForMobileSafari();
    },

    methods: {
      async updateDeviceMethod(method) {
        this.method = method;

        let methodID;
        if (method) {
          methodID = method.id;
        } else {
          methodID = null;
        }

        if (this.device.method_id !== methodID) {
          try {
            await apiDevices.updateDeviceMethod(this.id, methodID);
            this.device.method_id = methodID;
          } catch (e) {
            this.notifyError(`Can't select this method!`);
          }
        }
      },
      async updateDeviceColumn(column) {
        let columnID;
        if (column) {
          columnID = column.id;
        } else {
          columnID = null;
        }

        if (this.device.column_id !== columnID) {
          try {
            await apiDevices.updateDeviceColumn(this.id, columnID);
            this.device.column_id = columnID;
          } catch (e) {
            this.notifyError(`Can't select this column!`);
          }
        }
      },

      onConnectionConfirmed() {
        this.connectionLost = false;
        this.updateConnectionLostTimeout();
      },
      updateConnectionLostTimeout() {
        if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);
        this.timerConnectionLost = setTimeout(this.onConnectionLost, 4500);
      },
      onConnectionLost() {
        this.connectionLost = true;

        apiDevices.doNotSleep(this.id);

        this.updateConnectionLostTimeout();
      },

      createDeviceSocket(id) {
        this.destroyDeviceSocket();

        const socket = DeviceSocket.start(
          id,
          () => {
            apiDevices.doNotSleep(this.id);
          },
          (e) => {
            this.errorCode = e.code;
          },
        );

        const listenersGroup = socket.createEventListenersGroup();
        this.listenersGroupId = listenersGroup.id;

        listenersGroup.addEventListener(DeviceSocketEvents.ERROR, (error) => {
          this.notifyError(error);
        });

        return socket;
      },
      destroyDeviceSocket() {
        if (this.socket != null) this.socket.close(this.listenersGroupId);
        this.socket = null;
      },

      async saveName(name) {
        try {
          await apiDevices.updateDeviceName(this.id, name);
        } catch (e) {
          this.notifyError(`Can't update device name!`);
        }
      },

      settings() {
        this.$refs.settingsModal.show();
      },

      initDeviceData(deviceId) {
        this.stopSubscriptions();
        this.initSubscriptionForDevice(deviceId);
        this.initSubscriptionForDeviceState(deviceId);
        this.initSubscriptionForRecentInjections(deviceId);
      },
      stopSubscriptions() {
        this.subscriptionForDevice?.unsubscribe();
        this.subscriptionForDeviceState?.unsubscribe();
        this.subscriptionForRecentInjections?.unsubscribe();
      },

      initSubscriptionForDevice(deviceId) {
        this.subscriptionForDevice = apiDevices.subscribeForDevice(deviceId, (device) => {
          this.device = {
            ...device,
            isMethodCompatible:
              device.method_invalidated_reason !== METHOD_RESET_REASONS.CONFIGURATION_INCOMPATIBLE,
          };
          this.device.method_id = device.method?.id;
          this.device.column_id = device.column?.id;
          this.method = device.method || undefined;
          this.column = device.column;
        });
      },
      initSubscriptionForDeviceState(deviceId) {
        this.subscriptionForDeviceState = apiDevices.subscribeForDeviceState(
          deviceId,
          (deviceState) => {
            this.deviceState = deviceState;
          },
        );
      },
      initSubscriptionForRecentInjections(deviceId) {
        this.subscriptionForRecentInjections = apiInjections.subscribeForRecentInjections(
          deviceId,
          (injections) => {
            this.recentInjections = injections.map((injection) => ({
              ...injection,
              type: injection.sample.type,
            }));
          },
        );
      },

      onMethodNotValid(reason) {
        if (reason === METHOD_RESET_REASONS.CONFIGURATION_INCOMPATIBLE) {
          this.notify(
            'The device configuration was changed! Your method is not compatible now!',
            20,
          );
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .device-page {
    &__wrapper-title {
      align-items: baseline;
    }

    &__hardware {
      margin-top: 32px;

      @media (max-width: 932px) {
        margin-top: 16px;
        padding: 0 16px;
      }

      @media (max-width: $screen-xs-max) {
        margin-top: 8px;
        padding: 0 4px;
      }
    }
  }
</style>
